import { useState, useEffect } from 'react';
import { ChatInput } from '../chat/ChatInput';
import { ChatMessageList } from '../chat/ChatMessageList';
import { SummaryDisplay } from './SummaryDisplay';
import { useChat } from '../../hooks/useChat';
import { updateLearnerState, exportLearnerData } from '@/lib/api';
import { extractSummaries } from '@/lib/summary-extractor';
import type { LearnerState, Message } from '@/lib/types';

interface FinalReflectionProps {
  learnerId: string;
  moduleId: string;
  learnerState: LearnerState;
  onComplete: () => void;
  /** Transcript from a preceding voice session (shown above the text chat) */
  initialTranscript?: Array<{ role: 'user' | 'assistant'; content: string }>;
}

export function FinalReflection({
  learnerId,
  moduleId,
  learnerState,
  onComplete,
  initialTranscript,
}: FinalReflectionProps) {
  const { messages, streamingMessage, sendMessage, isLoading } = useChat({
    learnerId,
    moduleId,
    partId: 'final',
    phase: 'final',
  });

  const [synthesis, setSynthesis] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Collect all part summaries
  const partSummaries = Object.entries(learnerState.parts)
    .filter(([_, part]) => part.reflection?.summary)
    .map(([partId, part]) => ({
      partId,
      summary: part.reflection.summary,
    }));

  // Extract synthesis from streaming message
  useEffect(() => {
    if (streamingMessage) {
      const { summaries } = extractSummaries(streamingMessage);
      if (summaries.length > 0) {
        setSynthesis(summaries);
        setIsComplete(true);
      }
    }
  }, [streamingMessage]);

  // Clean messages for display
  const cleanedMessages = messages.map(msg => {
    if (msg.role === 'assistant') {
      const { cleanedMessage } = extractSummaries(msg.content);
      return { ...msg, content: cleanedMessage };
    }
    return msg;
  });

  const cleanedStreamingMessage = streamingMessage
    ? extractSummaries(streamingMessage).cleanedMessage
    : '';

  // Combine voice transcript with text chat messages
  const allMessages: Message[] = [
    ...(initialTranscript || []).map((m) => ({
      role: m.role,
      content: m.content,
    })),
    ...cleanedMessages,
  ];

  const handleComplete = async () => {
    setIsCompleting(true);

    try {
      const fullSynthesis = synthesis.join(' ');
      await updateLearnerState({
        learnerId,
        moduleId,
        finalReflectionUpdate: {
          synthesis: fullSynthesis,
          completed: true,
        },
      });

      onComplete();
    } catch (error) {
      console.error('Failed to save final reflection:', error);
      setIsCompleting(false);
    }
  };

  const handleSkip = async () => {
    setIsCompleting(true);

    try {
      const fullSynthesis = synthesis.join(' ') || 'Final reflection skipped';
      await updateLearnerState({
        learnerId,
        moduleId,
        finalReflectionUpdate: {
          synthesis: fullSynthesis,
          completed: false,
        },
      });

      onComplete();
    } catch (error) {
      console.error('Failed to save skip state:', error);
      setIsCompleting(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportLearnerData(learnerId);
    } catch (error) {
      console.error('Failed to export data:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-serif font-bold mb-3 text-slate-900 dark:text-slate-100">
          Final Reflection
        </h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">
          Let's synthesize your journey through human, AI, and collaborative intelligence
        </p>
      </div>

      {/* Previous Part Summaries */}
      {partSummaries.length > 0 && (
        <div className="mb-6">
          <SummaryDisplay
            summaries={partSummaries.map(p => p.summary)}
            title="Your Reflections So Far"
          />
        </div>
      )}

      {/* Synthesis Summary */}
      {synthesis.length > 0 && (
        <div className="mb-6">
          <SummaryDisplay
            summaries={synthesis}
            title="Synthesis"
          />
        </div>
      )}

      {/* Voice transcript banner — shown when switching from voice to text */}
      {initialTranscript && initialTranscript.length > 0 && (
        <div className="mb-4 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl px-5 py-3">
          <div className="flex items-center gap-2 text-violet-700 dark:text-violet-400 text-sm font-medium">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            </svg>
            Voice conversation continued below — you can keep going via text.
          </div>
        </div>
      )}

      {/* Chat */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 min-h-[500px] flex flex-col">
        <div className="flex flex-col h-full">
          {/* Initial Prompt (only if no voice transcript either) */}
          {allMessages.length === 0 && (
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-6 mb-6">
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                You've explored three different types of intelligence. Now let's bring it all together.
                What's one insight that stands out to you? How might it change how you approach teaching?
              </p>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 min-h-0 mb-4">
            <ChatMessageList
              messages={allMessages}
              streamingMessage={cleanedStreamingMessage}
            />
          </div>

          {/* Input or Completion */}
          {isComplete ? (
            <div className="text-center py-6">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 mb-4">
                <p className="text-green-800 dark:text-green-200 font-medium mb-2">
                  Module Complete!
                </p>
                <p className="text-green-700 dark:text-green-300 text-sm">
                  You've completed your journey through intelligence. Your insights have been saved.
                </p>
              </div>
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={handleComplete}
                  disabled={isCompleting}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all shadow-lg disabled:opacity-50"
                >
                  {isCompleting ? 'Saving...' : 'Complete Module →'}
                </button>
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="px-6 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {isExporting ? 'Exporting...' : 'Download Report'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <ChatInput
                onSendMessage={sendMessage}
                disabled={isLoading}
                placeholder="Share your final thoughts..."
              />
              <div className="flex justify-end px-4 pb-2">
                <button
                  onClick={handleSkip}
                  className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  Skip reflection →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
